// @flow
/**
 * Saving the draft as a version and asking for review, shared by the publish
 * sheet. Same mutation and cache updates the "Save as version" window used.
 */
import { gql, useMutation } from '@apollo/client';
import semver from 'semver';
import { incrementVersion } from '~/lib/getNextVersion';
import { toPageInput } from '../pageInput';
import {
  prependVersionSummary,
  patchPageEditorAfterSave,
  PAGE_VERSION_SUMMARIES_LIMIT,
} from './pageEditorCache';

export const COMMIT_VERSION = gql`
mutation commitVersion($id: String!, $page: UpdatePageInput, $versionId: String, $notes: String) {
  commitVersion(_id: $id, page: $page, versionId: $versionId, notes: $notes) {
    page {
      _id
      updatedAt
    }
    newVersion {
      _id
      versionId
      updatedAt
      updatedBy
      notes
    }
  }
}
`;

export const REQUEST_REVIEW = gql`
mutation requestPublishPage($pageId: ID!, $version: String!) {
  requestPublishPage(pageId: $pageId, version: $version) {
    status
    message
  }
}
`;

/** The next version number after the newest one (1.0.0 for the first). */
export const nextVersionId = (versions: Array<{ versionId: string }>) => {
  const ids = (versions || []).map(v => v.versionId).filter(v => semver.valid(v));
  if (!ids.length) return '1.0.0';
  ids.sort((a, b) => (semver.gt(a, b) ? -1 : 1));
  return incrementVersion(ids[0]);
};

/** updatedAt from the server as the unix-ms string the draft keeps. */
export const normalizeUpdatedAt = (updatedAt: mixed) => {
  if (updatedAt === null || updatedAt === undefined) return null;
  if (typeof updatedAt === 'number') return String(updatedAt);
  const raw = String(updatedAt);
  return /^\d+$/.test(raw) ? raw : String(new Date(raw).getTime());
};

export default function useVersionActions(pageId: string) {
  const [commit] = useMutation(COMMIT_VERSION, {
    refetchQueries: ['PageHistory'],
    update(cache, { data }) {
      const payload = data && data.commitVersion;
      if (!payload) return;
      prependVersionSummary(cache, pageId, payload.newVersion, PAGE_VERSION_SUMMARIES_LIMIT);
      patchPageEditorAfterSave(cache, pageId, payload.page);
    },
  });
  // The page's state and review fields change: refetch the editor's page.
  const [request] = useMutation(REQUEST_REVIEW, { refetchQueries: ['getPageEditor', 'PageHistory'] });

  /** Saves the draft as `versionId`; resolves to the page's new updatedAt (unix-ms string). */
  const saveVersion = async (draft: Object, lastUpdatedAt: ?Date, versionId: string, notes: string) => {
    const result = await commit({
      variables: { id: pageId, page: { ...toPageInput(draft), lastUpdatedAt }, versionId, notes }
    });
    const page = result && result.data && result.data.commitVersion && result.data.commitVersion.page;
    return normalizeUpdatedAt(page && page.updatedAt);
  };

  /** Asks for review of a saved version. Throws with the server's message when it refuses. */
  const requestReview = async (versionId: string) => {
    const result = await request({ variables: { pageId, version: versionId } });
    const response = result && result.data && result.data.requestPublishPage;
    if (!response || response.status !== 200) {
      throw new Error((response && response.message && response.message.trim()) || 'The review request didn’t go through.');
    }
  };

  return { saveVersion, requestReview };
}
